import { useMemo, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { Heart } from 'lucide-react';
import SystemDataService from '../../Services/SystemDataService';
import MediaComponent from '../Media/media-component';
import CountUp from '../Counter/count-up-component';
import { getDonationData } from '../../Public/donation-data';
import useRenderLog from '../../Hooks/useRenderLog';

/* ── Motion budget ───────────────────────────────────────────────────────────
 * On phones (and when the user prefers reduced motion) the heavy blur + infinite
 * float loops tank the frame rate, so we render a lighter, static variant. */
function useLightMotion() {
    const [light, setLight] = useState(false);
    useEffect(() => {
        if (typeof window === 'undefined' || !window.matchMedia) return;
        const mq = window.matchMedia('(max-width: 767px), (prefers-reduced-motion: reduce)');
        const update = () => setLight(mq.matches);
        update();
        mq.addEventListener('change', update);
        return () => mq.removeEventListener('change', update);
    }, []);
    return light;
}

/* ── Icons ───────────────────────────────────────────────────────────────── */
// The standard "favorites" heart (lucide), solid-filled.
function HeartIcon({ className = 'h-5 w-5' }) {
    return <Heart className={className} fill="currentColor" strokeWidth={0} />;
}
function HeartOutline({ className = 'h-5 w-5' }) {
    return <Heart className={className} fill="currentColor" strokeWidth={0} />;
}

/* ── Animated number ─────────────────────────────────────────────────────────
 * Values arrive as strings with symbols ("$24,000", "230+", "+47"). Split off a
 * prefix/suffix and count up just the numeric part when it scrolls into view. */
function CountUpValue({ value, duration = 1.6 }) {
    const raw = String(value ?? '');
    const match = raw.match(/[\d.,]+/);
    if (!match) return <>{raw}</>;
    const numStr = match[0];
    const to = parseFloat(numStr.replace(/,/g, ''));
    if (!Number.isFinite(to)) return <>{raw}</>;
    return (
        <>
            {raw.slice(0, match.index)}
            <CountUp to={to} duration={duration} separator={numStr.includes(',') ? ',' : ''} />
            {raw.slice(match.index + numStr.length)}
        </>
    );
}
function ArrowIcon({ className = 'h-5 w-5' }) {
    return (<svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.2}><path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" /></svg>);
}
function UsersIcon({ className = 'h-5 w-5' }) {
    return (<svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round"><path d="M17 20v-1.5a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4V20" /><circle cx="9.5" cy="7.5" r="3.5" /><path d="M22 20v-1.5a4 4 0 0 0-3-3.87M16 4.13a4 4 0 0 1 0 7.75" /></svg>);
}
function LockIcon({ className = 'h-3.5 w-3.5' }) {
    return (<svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><rect x="4.5" y="10.5" width="15" height="10" rx="2" /><path d="M8 10.5V7a4 4 0 0 1 8 0v3.5" /></svg>);
}

/* ── DONATE headline with a heart inside the "O" ─────────────────────────── */
function DonateTitle({ text }) {
    const idx = text.toUpperCase().indexOf('O');
    if (idx === -1) {
        return (
            <h2 className="font-secondary font-extralight text-white uppercase leading-none inline-flex items-center gap-3">
                <span>{text}</span>
                <HeartIcon className="text-secondary-500 h-[0.7em] w-[0.7em]" />
            </h2>
        );
    }
    return (
        <h2 className="font-secondary font-extralight text-white uppercase leading-none inline-flex items-center">
            <span>{text.slice(0, idx)}</span>
            <span className="inline-flex items-center justify-center rounded-full border-white border-[0.12em] w-[0.82em] h-[0.82em] align-middle mx-[0.02em]">
                <HeartIcon className="text-secondary-500 w-[0.55em] h-[0.55em]" />
            </span>
            <span>{text.slice(idx + 1)}</span>
        </h2>
    );
}

/* ── Collage card ────────────────────────────────────────────────────────── */
function CornerCard({ src, positionClass, rotate = 0, delay = 0, float = true }) {
    const inner = (
        <>
            <MediaComponent src={src} alt="" width={640} quality={75} useSekeleton={false} wrapperClassName="w-full h-full" />
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-primary-950/50 to-transparent" />
        </>
    );
    return (
        <motion.div
            className={`absolute rounded-2xl overflow-hidden ring-1 ring-secondary-500/40 shadow-2xl shadow-black/70 ${positionClass}`}
            initial={{ opacity: 0, scale: 0.85 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay }}
            style={{ rotate }}
        >
            {float ? (
                <motion.div className="w-full h-full" animate={{ y: [0, -8, 0] }} transition={{ duration: 6 + delay * 3, repeat: Infinity, ease: 'easeInOut', delay }}>
                    {inner}
                </motion.div>
            ) : (
                <div className="w-full h-full">{inner}</div>
            )}
        </motion.div>
    );
}

/* ── Circular progress ring ──────────────────────────────────────────────── */
function ProgressRing({ percent = 0 }) {
    const r = 54;
    const C = 2 * Math.PI * r;
    const offset = C * (1 - Math.min(100, Math.max(0, percent)) / 100);
    return (
        <div className="relative h-24 w-24 shrink-0">
            <svg viewBox="0 0 128 128" className="h-full w-full -rotate-90">
                <circle cx="64" cy="64" r={r} fill="none" strokeWidth="10" className="text-secondary-100" stroke="currentColor" />
                <motion.circle
                    cx="64" cy="64" r={r} fill="none" strokeWidth="10" strokeLinecap="round"
                    className="text-secondary-500" stroke="currentColor" strokeDasharray={C}
                    initial={{ strokeDashoffset: C }}
                    whileInView={{ strokeDashoffset: offset }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.9, ease: 'easeOut' }}
                />
            </svg>
            <span className="absolute inset-0 flex items-center justify-center text-secondary-600 font-bold text-2xl">
                <CountUpValue value={`${percent}%`} />
            </span>
        </div>
    );
}

/* ── Section ─────────────────────────────────────────────────────────────── */
function DonationSection({ data, onCta }) {
    useRenderLog('DonationSection', { data, onCta });
    // Select only the fields this section uses (as primitives / a single ref) so
    // it doesn't re-render when unrelated systemData slices (categories, services,
    // banners, businessInfo) load.
    const dir = useSelector((s) => s.systemData?.dir || 'rtl');
    const language = useSelector((s) => s.systemData?.language || 'he');
    const links = useSelector((s) => s.systemData?.links || []);
    const brandName = useSelector((s) => s.systemData?.businessInfo?.businessName || '');

    const d = useMemo(() => data || getDonationData(language), [data, language]);
    const logoWhite = SystemDataService.byLabel(links, 'Logo White');
    const lightMotion = useLightMotion();

    const {
        images = [], title, subtitle, eyebrow,
        headingPre, headingScript, headingPost,
        goalLabel, percent = 0, raised, goal,
        donorExtra, donorAvatars = [],
        membersPre, membersHighlight, membersPost,
        ctaText, donateUrl, secureNote,
    } = d || {};

    const collage = images.filter(Boolean);

    const cornerLayout = [
        { positionClass: 'top-[3%] left-[1%] w-[24%] aspect-square', rotate: -6, delay: 0.05 },
        { positionClass: 'top-[4%] right-[1%] w-[26%] aspect-[3/4]', rotate: 5, delay: 0.15 },
        { positionClass: 'top-[46%] left-[1%] w-[24%] aspect-[4/3]', rotate: 4, delay: 0.25 },
        { positionClass: 'top-[48%] right-[1%] w-[24%] aspect-[3/4]', rotate: -5, delay: 0.35 },
    ];


    return (
        <section dir={dir} className="relative w-full bg-primary-950 overflow-hidden">
            {/* Ambient glow */}
            <div className="pointer-events-none absolute -top-1/4 left-1/2 -translate-x-1/2 h-[60vh] w-[60vh] rounded-full bg-secondary-800/25 blur-2xl md:blur-[130px]" />

            {/* On desktop the headline/collage and the mission card sit side by side; they stack on mobile. */}
            <div className="relative z-10 max-w-[1120px] mx-auto px-6 py-12 md:py-16 grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14 items-center">

            {/* ── logo + DONATE + collage (rendered second: card sits on top/start) ── */}
            <div className="relative order-1">
                <div className="relative min-h-[42vh] lg:min-h-[30rem] flex flex-col items-center justify-center text-center">

                    {/* Decorative dashed orbit + dots */}
                    <div className="pointer-events-none absolute left-1/2 top-1/2 h-[82%] w-[94%] -translate-x-1/2 -translate-y-1/2 rounded-full border border-dashed border-white/10" />
                    <span className="absolute left-[10%] top-[38%] h-2 w-2 rounded-full bg-secondary-500" />
                    <span className="absolute right-[12%] top-[16%] h-1.5 w-1.5 rounded-full bg-secondary-400" />
                    <span className="absolute left-[30%] bottom-[10%] h-1.5 w-1.5 rounded-full bg-secondary-500" />

                    {collage.map((src, i) => (
                        <CornerCard key={i} src={src} float={!lightMotion} {...cornerLayout[i % cornerLayout.length]} />
                    ))}

                    <div className="relative z-10 flex flex-col items-center gap-4 px-4">
                        {logoWhite?.image && (
                            <img src={logoWhite.image} alt={brandName || 'Logo'} className="h-12 md:h-14 w-auto object-contain drop-shadow-lg" />
                        )}

                        <div className="mt-1 text-5xl md:text-6xl">
                            <DonateTitle text={title} />
                        </div>

                        {subtitle && (
                            <p className="text-white/60 text-sm md:text-base leading-relaxed max-w-sm">
                                {subtitle}
                            </p>
                        )}
                    </div>
                </div>
            </div>

            {/* ── White mission card (rendered first: on top on mobile, start side on desktop) ── */}
            <div className="relative z-10 order-2 w-full max-w-[560px] mx-auto lg:mx-0">
                <div className="rounded-3xl bg-white shadow-2xl shadow-black/40 px-6 md:px-8 py-8 flex flex-col items-center text-center">

                    {eyebrow && (
                        <span className="text-secondary-500 text-xs md:text-sm font-bold uppercase">
                            {eyebrow}
                        </span>
                    )}

                    <h3 className="mt-3 text-primary-900 font-bold leading-snug text-xl md:text-2xl">
                        {headingPre}{' '}
                        <span className="text-secondary-500 font-script font-normal text-[1.35em]">
                            {headingScript}
                        </span>{' '}
                        {headingPost}
                        <HeartOutline className="inline-block h-5 w-5 text-secondary-400 align-top ml-2" />
                    </h3>

                    {/* Progress card */}
                    <div className="w-full mt-6 rounded-2xl bg-secondary-50/60 ring-1 ring-secondary-100 px-5 py-5 flex flex-col sm:flex-row items-center gap-5">
                        <ProgressRing percent={percent} />

                        <div dir={dir} className="flex-1 flex flex-col items-center sm:items-start gap-0.5 text-center sm:text-start">
                            {goalLabel && <span className="text-primary-400 text-xs font-semibold uppercase">{goalLabel}</span>}
                            {raised && <span className="text-secondary-600 font-bold text-2xl md:text-3xl leading-none"><CountUpValue value={raised} /></span>}
                            {goal && <span className="text-primary-400 text-sm"><CountUpValue value={goal} /></span>}

                            <div dir="ltr" className="flex items-center -space-x-2 mt-2.5">
                                {donorAvatars.filter(Boolean).map((src, i) => (
                                    <span key={i} className="h-8 w-8 rounded-full ring-2 ring-white overflow-hidden bg-secondary-100">
                                        <MediaComponent src={src} alt="" width={96} quality={70} useSekeleton={false} wrapperClassName="w-full h-full" />
                                    </span>
                                ))}
                                {donorExtra && (
                                    <span className="h-8 w-8 rounded-full ring-2 ring-white bg-secondary-500 text-white font-bold text-[10px] flex items-center justify-center">
                                        <CountUpValue value={donorExtra} />
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Divider */}
                    <div className="w-full h-px bg-primary-100 my-6" />

                    {/* Members line */}
                    <div className="flex items-center gap-3.5 text-start">
                        <span className="shrink-0 h-9 w-9 rounded-full bg-secondary-100 text-secondary-600 flex items-center justify-center">
                            <UsersIcon className="h-4 w-4" />
                        </span>
                        <p className="text-primary-500 text-sm md:text-base leading-snug">
                            {membersPre} <span className="text-secondary-600 font-bold"><CountUpValue value={membersHighlight} /></span> {membersPost}
                        </p>
                    </div>

                    {/* CTA */}
                    <a
                        href={donateUrl || '/contact'}
                        target={donateUrl ? '_blank' : undefined}
                        rel="noopener noreferrer"
                        onClick={onCta}
                        dir="ltr"
                        className="group w-full mt-6 flex items-center justify-center gap-3 rounded-full bg-gradient-to-r from-secondary-500 to-secondary-700 px-6 py-4 text-white font-bold uppercase text-sm shadow-lg shadow-secondary-500/30 transition-all duration-300 hover:from-secondary-600 hover:to-secondary-800 active:scale-[0.98]"
                    >
                        <HeartOutline className="h-4 w-4 shrink-0" />
                        <span>{ctaText}</span>
                    </a>

                    {secureNote && (
                        <p className="mt-3 flex items-center justify-center gap-1.5 text-primary-400 text-xs">
                            <LockIcon className="h-3.5 w-3.5" />
                            {secureNote}
                        </p>
                    )}
                </div>
            </div>

            </div>{/* end desktop 2-col grid */}
        </section>
    );
}

export default DonationSection;
